<template>
  <div class="ops-page">
    <!-- 编辑气泡 -->
    <Modal
      v-model="modalNotice"
      title="编辑公告栏气泡"
      :mask-closable="false"
      :closable="false"
    >
      <Form :label-width="100">
        <FormItem label="气泡类型" required>
          <Select v-model="noticeForm.notice_type" style="width: 100%">
            <Option :value="0">不气泡不跳转，弹 toast</Option>
            <Option :value="1">不气泡但跳转</Option>
            <Option :value="2">需气泡引导且跳转</Option>
          </Select>
        </FormItem>
        <FormItem label="气泡文案" required>
          <Input v-model="noticeForm.notice_info" placeholder="请输入气泡文案" clearable />
        </FormItem>
        <FormItem label="截止日期" required>
          <DatePicker
            v-model="noticeForm.expire_date"
            type="date"
            format="yyyy-MM-dd"
            placeholder="选择截止日期"
            style="width: 100%"
          />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button @click="modalNotice = false">取消</Button>
        <Button type="primary" :loading="noticeSubmitting" @click="submitNotice">保存</Button>
      </div>
    </Modal>

    <!-- 新建活动 -->
    <Modal
      v-model="modalCreate"
      title="新建活动"
      :mask-closable="false"
      :closable="false"
      width="640"
    >
      <Form :label-width="100">
        <FormItem label="标题" required>
          <Input v-model="createForm.title" clearable />
        </FormItem>
        <FormItem label="封面" required>
          <div class="cover-upload">
            <Upload
              action=""
              :before-upload="beforeCoverUpload"
              :show-upload-list="false"
              accept=".jpg,.jpeg,.png"
            >
              <Button icon="ios-cloud-upload-outline">选择封面</Button>
            </Upload>
            <span class="cover-tip">465×264，jpg/png，&lt;150KB；提交时上传</span>
          </div>
          <div v-if="coverPreviewUrl" class="cover-preview">
            <Poptip trigger="hover" transfer title="封面预览">
              <div slot="content" class="cover-pop">
                <img :src="coverPreviewUrl" alt="cover" />
              </div>
              <img :src="coverPreviewUrl" class="cover-thumb" alt="cover" />
            </Poptip>
          </div>
        </FormItem>
        <FormItem label="介绍" required>
          <Input v-model="createForm.introduce" type="textarea" :autosize="{minRows: 3}" />
        </FormItem>
        <FormItem label="开始时间" required>
          <DatePicker
            v-model="createForm.start_time"
            type="date"
            format="yyyy-MM-dd"
            placeholder="选择开始日期"
            style="width: 100%"
          />
        </FormItem>
        <FormItem label="结束时间">
          <DatePicker
            v-model="createForm.end_time"
            type="date"
            format="yyyy-MM-dd"
            placeholder="长期活动可留空"
            style="width: 100%"
          />
        </FormItem>
        <FormItem label="打开网页地址">
          <Input v-model="createForm.url" clearable placeholder="可选，填写后可打开网页" />
        </FormItem>
        <FormItem label="App跳转">
          <Select v-model="createForm.app_url" style="width: 100%" clearable placeholder="空">
            <Option
              v-for="item in appUrlOptions"
              :key="item.value === '' ? '__empty__' : item.value"
              :value="item.value"
            >{{ item.label }}</Option>
          </Select>
        </FormItem>
      </Form>
      <p class="create-note">注：App跳转和打开网页为二选一，都为空则仅展示；不填写结束时间则为长期活动</p>
      <div slot="footer">
        <Button @click="closeCreate">取消</Button>
        <Button type="primary" :loading="createSubmitting" @click="submitCreate">提交</Button>
      </div>
    </Modal>

    <!-- 气泡卡片 -->
    <div class="notice-card">
      <div class="notice-card-body">
        <div class="notice-card-title">当前公告栏气泡</div>
        <template v-if="hasNoticeConfig">
          <p><span class="label">类型：</span>{{ noticeTypeLabel(noticeBoard.notice_type) }}</p>
          <p><span class="label">文案：</span>{{ noticeBoard.notice_info || '—' }}</p>
          <p><span class="label">过期时间：</span>{{ noticeBoard.expired_time || '—' }}</p>
        </template>
        <p v-else class="empty-tip">当前无气泡配置</p>
      </div>
      <div class="notice-card-actions">
        <Button type="primary" @click="openNoticeEdit">编辑</Button>
        <Button style="margin-left: 8px" :disabled="!hasNoticeConfig" @click="clearNotice">清除</Button>
        <Button style="margin-left: 8px" @click="getList">刷新</Button>
      </div>
    </div>

    <!-- 活动列表 -->
    <div class="table-head">
      <div class="table-title-wrap">
        <span class="table-title">活动列表</span>
        <span class="table-tip">（展示同客户端，先按是否过期分组，再按排序从大到小）</span>
      </div>
      <Button type="primary" @click="openCreate">新建活动</Button>
    </div>
    <Table :columns="columns" :data="records">
      <template slot-scope="{ row }" slot="cover">
        <Poptip v-if="resolveCover(row.cover)" trigger="hover" transfer title="封面预览">
          <div slot="content" class="cover-pop">
            <img :src="resolveCover(row.cover)" alt="cover" />
          </div>
          <img :src="resolveCover(row.cover)" class="cover-thumb" alt="cover" />
        </Poptip>
        <span v-else>—</span>
      </template>
      <template slot-scope="{ row }" slot="introduce">
        <Tooltip transfer max-width="320">
          <div slot="content" class="introduce-tip">{{ row.introduce }}</div>
          <div class="ellipsis-4" v-html="formatIntroduceHtml(row.introduce)"></div>
        </Tooltip>
      </template>
      <template slot-scope="{ row }" slot="valid_time">
        <span>{{ formatValidTime(row) }}</span>
      </template>
      <template slot-scope="{ row }" slot="open_desc">
        <template v-if="!hasUrl(row) && !hasAppUrl(row)">
          <span>仅展示</span>
        </template>
        <template v-else-if="hasUrl(row) && !hasAppUrl(row)">
          <Button type="primary" size="small" ghost @click="openWebUrl(row.url)">打开网页</Button>
        </template>
        <template v-else-if="!hasUrl(row) && hasAppUrl(row)">
          <span>{{ appUrlLabel(row.app_url) }}</span>
        </template>
        <template v-else>
          <div>
            <div>{{ appUrlLabel(row.app_url) }}</div>
            <Button type="primary" size="small" ghost style="margin-top: 4px" @click="openWebUrl(row.url)">打开网页</Button>
          </div>
        </template>
      </template>
      <template slot-scope="{ row }" slot="action">
        <div class="action-btns">
          <Button
            class="btn-pin"
            size="small"
            :loading="toppingId === row.id"
            :disabled="isAlreadyTop(row) || (toppingId !== null && toppingId !== row.id)"
            @click="pinToTop(row)"
          >置顶</Button>
          <Button
            v-if="row.deleted === 0"
            size="small"
            type="error"
            ghost
            @click="softDelete(row)"
          >删除</Button>
          <Button
            v-else
            size="small"
            @click="restore(row)"
          >恢复</Button>
        </div>
      </template>
    </Table>
  </div>
</template>

<script>
import apis from '../../http/api'
import OSS from 'ali-oss'

const CDN_HOST = 'https://image.co-study.com.cn/'
const COVER_DIR = 'app/image/notice_board/'
const COVER_WIDTH = 465
const COVER_HEIGHT = 264
const COVER_MAX_BYTES = 150 * 1024

const NOTICE_TYPE_MAP = {
  0: '不气泡不跳转，弹 toast',
  1: '不气泡但跳转',
  2: '需气泡引导且跳转',
}

/** App 跳转页选项：后期新增页面只改这里即可 */
const APP_URL_OPTIONS = [
  { value: '', label: '空' },
  { value: '/page/buy_jewel', label: '充值中心页面' },
  { value: '/page/buy_membership', label: 'Co会员页面' },
]

const APP_URL_LABEL_MAP = APP_URL_OPTIONS.reduce((map, item) => {
  map[item.value] = item.label
  return map
}, {})

/**
 * 接口只返回图片文件名；展示对齐头像等模块：
 * https://image.co-study.com.cn/app/image/notice_board/{filename}
 */
function resolveCoverUrl(cover) {
  if (!cover) {
    return ''
  }
  let name = String(cover).trim()
  if (!name) {
    return ''
  }
  // 兼容误存的完整 URL / 带目录路径，只取文件名
  if (name.includes('/')) {
    name = name.split('/').pop()
  }
  return CDN_HOST + COVER_DIR + name
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatFileTs(date = new Date()) {
  return (
    `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}` +
    `${pad2(date.getHours())}${pad2(date.getMinutes())}${pad2(date.getSeconds())}`
  )
}

function formatDateOnly(value) {
  if (!value) {
    return ''
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) {
    return ''
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** 默认截止日期：明天 */
function defaultExpireDate() {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(0, 0, 0, 0)
  return d
}

/** 截止日 23:59:59 相对当前时刻的秒数 */
function calcExpiredSeconds(dateValue) {
  const dateOnly = formatDateOnly(dateValue)
  if (!dateOnly) {
    return 0
  }
  const end = new Date(`${dateOnly.replace(/-/g, '/')} 23:59:59`)
  if (Number.isNaN(end.getTime())) {
    return 0
  }
  return Math.floor((end.getTime() - Date.now()) / 1000)
}

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('无法读取图片'))
    }
    img.src = url
  })
}

export default {
  name: 'OpsNoticeBoard',
  data() {
    return {
      ossClient: null,
      records: [],
      appUrlOptions: APP_URL_OPTIONS,
      noticeBoard: {
        notice_info: '',
        notice_type: 0,
        notice_url: '',
        expired_time: '',
      },
      modalNotice: false,
      noticeSubmitting: false,
      noticeForm: {
        notice_type: 2,
        notice_info: '',
        expire_date: null,
      },
      modalCreate: false,
      createSubmitting: false,
      coverFile: null,
      coverPreviewUrl: '',
      createForm: {
        title: '',
        introduce: '',
        start_time: null,
        end_time: null,
        url: '',
        app_url: '',
      },
      toppingId: null,
      columns: [
        { title: '标题', key: 'title', width: 180, align: 'center' },
        { title: '封面', slot: 'cover', width: 155, className: 'cover-col', align: 'center' },
        { title: '介绍', slot: 'introduce', minWidth: 140, align: 'left', className: 'introduce-col' },
        { title: '有效期', slot: 'valid_time', width: 220, align: 'center' },
        { title: '打开说明', slot: 'open_desc', width: 140, align: 'center' },
        { title: '排序', key: 'sort', width: 70, align: 'center' },
        { title: '操作', slot: 'action', width: 160, fixed: 'right', align: 'center' },
      ],
    }
  },
  computed: {
    hasNoticeConfig() {
      const info = (this.noticeBoard.notice_info || '').trim()
      return !!info
    },
  },
  created() {
    this.getList()
  },
  methods: {
    resolveCover(cover) {
      return resolveCoverUrl(cover)
    },
    formatIntroduceHtml(text) {
      if (!text) {
        return ''
      }
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r\n|\r|\n/g, '<br>')
    },
    formatValidTime(row) {
      const end = (row.end_time || '').trim()
      if (!end) {
        return '长期有效'
      }
      const startDate = this.toDateOnly(row.start_time)
      const endDate = this.toDateOnly(row.end_time)
      if (!startDate || !endDate) {
        return '长期有效'
      }
      return `${startDate} — ${endDate}`
    },
    toDateOnly(value) {
      if (!value) {
        return ''
      }
      const text = String(value).trim()
      // 兼容 "yyyy-MM-dd HH:mm:ss" / "yyyy-MM-dd"
      const m = text.match(/^(\d{4}-\d{2}-\d{2})/)
      return m ? m[1] : ''
    },
    hasUrl(row) {
      return !!(row.url || '').trim()
    },
    hasAppUrl(row) {
      return !!(row.app_url || '').trim()
    },
    appUrlLabel(appUrl) {
      const key = (appUrl || '').trim()
      if (!key) {
        return APP_URL_LABEL_MAP[''] || '空'
      }
      return Object.prototype.hasOwnProperty.call(APP_URL_LABEL_MAP, key)
        ? APP_URL_LABEL_MAP[key]
        : '未知页面'
    },
    openWebUrl(url) {
      const target = (url || '').trim()
      if (!target) {
        return
      }
      window.open(target, '_blank')
    },
    noticeTypeLabel(type) {
      return NOTICE_TYPE_MAP[type] || `未知(${type})`
    },
    getOss() {
      return apis.get_oss().then(res => {
        if (!res || !res.AccessKeyId || !res.AccessKeySecret || !res.SecurityToken) {
          throw new Error('OSS STS 凭证不完整')
        }
        this.ossClient = new OSS({
          region: 'oss-cn-shanghai',
          accessKeyId: res.AccessKeyId,
          accessKeySecret: res.AccessKeySecret,
          stsToken: res.SecurityToken,
          bucket: 'co-study',
        })
        return this.ossClient
      })
    },
    getList() {
      apis.app_activity_list().then(res => {
        const { data, errCode, msg } = res
        if (errCode === 0) {
          this.records = (data && data.records) || []
          this.noticeBoard = (data && data.notice_board) || {
            notice_info: '',
            notice_type: 0,
            notice_url: '',
            expired_time: '',
          }
        } else {
          this.$Message.error(msg || '获取活动列表失败')
        }
      }).catch(() => {
        this.$Message.error('获取活动列表失败')
      })
    },
    openNoticeEdit() {
      this.noticeForm = {
        notice_type: this.hasNoticeConfig ? (this.noticeBoard.notice_type || 0) : 2,
        notice_info: this.noticeBoard.notice_info || '',
        expire_date: defaultExpireDate(),
      }
      this.modalNotice = true
    },
    submitNotice() {
      if (this.noticeSubmitting) {
        return
      }
      const type = this.noticeForm.notice_type
      if (type !== 0 && type !== 1 && type !== 2) {
        this.$Message.error('请选择气泡类型')
        return
      }
      if (!(this.noticeForm.notice_info || '').trim()) {
        this.$Message.error('请输入气泡文案')
        return
      }
      if (!formatDateOnly(this.noticeForm.expire_date)) {
        this.$Message.error('请选择截止日期')
        return
      }
      const expiredTime = calcExpiredSeconds(this.noticeForm.expire_date)
      if (expiredTime <= 0) {
        this.$Message.error('截止日期须晚于当前时间')
        return
      }
      this.noticeSubmitting = true
      apis.app_activity_notice({
        notice_type: type,
        notice_info: this.noticeForm.notice_info.trim(),
        expired_time: expiredTime,
      }).then(res => {
        const { errCode, msg } = res
        if (errCode === 0) {
          this.$Message.success('气泡已更新')
          this.modalNotice = false
          this.getList()
        } else {
          this.$Message.error(msg || '更新失败')
        }
      }).catch(() => {
        this.$Message.error('更新失败')
      }).finally(() => {
        this.noticeSubmitting = false
      })
    },
    clearNotice() {
      this.$Modal.confirm({
        title: '清除气泡',
        content: '确认清除当前公告栏气泡？',
        onOk: () => {
          return apis.app_activity_notice({ notice_type: -1 }).then(res => {
            const { errCode, msg } = res
            if (errCode === 0) {
              this.$Message.success('已清除')
              this.getList()
            } else {
              this.$Message.error(msg || '清除失败')
              return Promise.reject()
            }
          })
        },
      })
    },
    openCreate() {
      this.clearCoverLocal()
      this.createForm = {
        title: '',
        introduce: '',
        start_time: null,
        end_time: null,
        url: '',
        app_url: '',
      }
      this.modalCreate = true
    },
    closeCreate() {
      this.modalCreate = false
      this.clearCoverLocal()
    },
    clearCoverLocal() {
      if (this.coverPreviewUrl) {
        URL.revokeObjectURL(this.coverPreviewUrl)
      }
      this.coverFile = null
      this.coverPreviewUrl = ''
    },
    beforeCoverUpload(file) {
      this.pickCover(file)
      return false
    },
    async pickCover(file) {
      const ext = (file.name.split('.').pop() || '').toLowerCase()
      if (!['jpg', 'jpeg', 'png'].includes(ext)) {
        this.$Message.error('仅支持 jpg / png 格式')
        return
      }
      if (file.size >= COVER_MAX_BYTES) {
        this.$Message.error('图片须小于 150KB')
        return
      }
      try {
        const { width, height } = await readImageSize(file)
        if (width !== COVER_WIDTH || height !== COVER_HEIGHT) {
          this.$Message.error(`图片尺寸必须为 ${COVER_WIDTH}×${COVER_HEIGHT}`)
          return
        }
      } catch (e) {
        this.$Message.error(e.message || '无法读取图片')
        return
      }
      this.clearCoverLocal()
      this.coverFile = file
      this.coverPreviewUrl = URL.createObjectURL(file)
    },
    async uploadCoverToOss(file) {
      const ext = (file.name.split('.').pop() || '').toLowerCase()
      const fileName = `activity_${formatFileTs()}.${ext === 'jpeg' ? 'jpg' : ext}`
      const objectKey = COVER_DIR + fileName
      await this.getOss()
      if (!this.ossClient) {
        throw new Error('OSS STS 刷新失败，请重试')
      }
      const result = await this.ossClient.put(objectKey, file)
      if (!result || !result.res || result.res.status !== 200) {
        throw new Error('上传至 OSS 失败')
      }
      return fileName
    },
    async submitCreate() {
      if (this.createSubmitting) {
        return
      }
      if (!(this.createForm.title || '').trim()) {
        this.$Message.error('请输入标题')
        return
      }
      if (!this.coverFile) {
        this.$Message.error('请选择封面')
        return
      }
      if (!(this.createForm.introduce || '').trim()) {
        this.$Message.error('请输入介绍')
        return
      }
      const startDate = formatDateOnly(this.createForm.start_time)
      if (!startDate) {
        this.$Message.error('请选择开始时间')
        return
      }
      const webUrl = (this.createForm.url || '').trim()
      const appUrl = (this.createForm.app_url || '').trim()
      if (webUrl && appUrl) {
        this.$Message.error('App跳转和打开网页为二选一，都为空则仅展示')
        return
      }
      if (webUrl && !/^https?:\/\//i.test(webUrl)) {
        this.$Message.error('打开网页地址须以 http:// 或 https:// 开头')
        return
      }
      this.createSubmitting = true
      try {
        // 提交时再刷新 STS 并上传封面
        const coverName = await this.uploadCoverToOss(this.coverFile)
        const payload = {
          title: this.createForm.title.trim(),
          cover: coverName,
          introduce: this.createForm.introduce.trim(),
          start_time: `${startDate} 00:00:00`,
          url: webUrl,
          app_url: appUrl,
          sort: this.nextSortValue(),
        }
        const endDate = formatDateOnly(this.createForm.end_time)
        if (endDate) {
          payload.end_time = `${endDate} 23:59:59`
        }
        const res = await apis.app_activity_create(payload)
        const { errCode, msg } = res
        if (errCode === 0) {
          this.$Message.success('新建成功')
          this.modalCreate = false
          this.clearCoverLocal()
          this.getList()
        } else {
          this.$Message.error(msg || '新建失败')
        }
      } catch (e) {
        console.log(e)
        this.$Message.error((e && e.message) || '新建失败')
      } finally {
        this.createSubmitting = false
      }
    },
    nextSortValue() {
      const sorts = (this.records || []).map(item => Number(item.sort) || 0)
      const maxSort = sorts.length ? Math.max(...sorts) : 0
      return maxSort + 1
    },
    maxSortValue() {
      const sorts = (this.records || []).map(item => Number(item.sort) || 0)
      return sorts.length ? Math.max(...sorts) : 0
    },
    isAlreadyTop(row) {
      return Number(row.sort) === this.maxSortValue()
    },
    pinToTop(row) {
      if (this.toppingId !== null || this.isAlreadyTop(row)) {
        return
      }
      const nextSort = this.nextSortValue()
      this.toppingId = row.id
      apis.app_activity_update({
        id: row.id,
        sort: nextSort,
      }).then(res => {
        const { errCode, msg } = res
        if (errCode === 0) {
          this.$Message.success('已置顶')
          this.getList()
        } else {
          this.$Message.error(msg || '置顶失败')
        }
      }).catch(() => {
        this.$Message.error('置顶失败')
      }).finally(() => {
        this.toppingId = null
      })
    },
    softDelete(row) {
      this.$Modal.confirm({
        title: '删除活动',
        content: `确认软删活动「${row.title}」？`,
        onOk: () => {
          return apis.app_activity_update({ id: row.id, deleted: 1 }).then(res => {
            const { errCode, msg } = res
            if (errCode === 0) {
              this.$Message.success(`已删除「${row.title}」`)
              this.getList()
            } else {
              this.$Message.error(msg || '删除失败')
              return Promise.reject()
            }
          })
        },
      })
    },
    restore(row) {
      this.$Modal.confirm({
        title: '恢复活动',
        content: `确认恢复活动「${row.title}」？`,
        onOk: () => {
          return apis.app_activity_update({ id: row.id, deleted: 0 }).then(res => {
            const { errCode, msg } = res
            if (errCode === 0) {
              this.$Message.success('已恢复')
              this.getList()
            } else {
              this.$Message.error(msg || '恢复失败')
              return Promise.reject()
            }
          })
        },
      })
    },
  },
}
</script>

<style scoped>
.ops-page {
  padding: 16px;
}

.notice-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  margin-bottom: 16px;
  background: #f8f8f9;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.notice-card-title {
  font-weight: bold;
  margin-bottom: 8px;
}

.notice-card-body p {
  margin: 4px 0;
  line-height: 1.6;
}

.notice-card-body .label {
  color: #808695;
}

.empty-tip {
  color: #808695;
}

.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.table-title-wrap {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}

.table-title {
  font-weight: bold;
  font-size: 16px;
}

.table-tip {
  margin-left: 6px;
  color: #808695;
  font-size: 13px;
  font-weight: normal;
}

.create-note {
  margin: 8px 0 0;
  color: #808695;
  font-size: 12px;
  line-height: 1.6;
}

.action-btns {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 6px;
}

.action-btns .btn-pin {
  min-width: 58px;
}

.cover-upload {
  display: flex;
  align-items: center;
}

.cover-tip {
  margin-left: 8px;
  color: #808695;
  font-size: 12px;
}

.cover-pop img {
  max-width: 465px;
  max-height: 264px;
  display: block;
}

.cover-preview {
  margin-top: 8px;
}

.cover-preview .cover-thumb {
  display: block;
  width: 155px;
  height: 88px;
  object-fit: contain;
  cursor: pointer;
}

.cover-thumb {
  display: block;
  width: 155px;
  height: 88px;
  object-fit: contain;
  cursor: pointer;
}

.ellipsis-4 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.4;
  max-height: calc(1.4em * 4);
  text-align: left;
  width: 100%;
}

.introduce-tip {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 320px;
  text-align: left;
}
</style>

<style>
/* Table 单元格默认左右 padding 18px，会把 155 宽缩略图裁掉；封面列去掉左右内边距 */
.ops-page .ivu-table td.cover-col,
.ops-page .ivu-table th.cover-col {
  padding-left: 0;
  padding-right: 0;
}
.ops-page .ivu-table td.cover-col .ivu-table-cell {
  padding-left: 0;
  padding-right: 0;
  overflow: visible;
}
.ops-page .ivu-table td.introduce-col .ivu-table-cell {
  text-align: left;
}
.ops-page .ivu-table td.introduce-col .ivu-tooltip,
.ops-page .ivu-table td.introduce-col .ivu-tooltip-rel {
  display: block;
  width: 100%;
  text-align: left;
}
</style>
